var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsxs as _jsxs, jsx as _jsx } from "react/jsx-runtime";
import { useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { DataContext } from '../player/DataContext';
import customEventHandler from '../helper/customEvents';
import { t } from 'i18next';
import { announce } from '../helper/announcer';
import '../styles/stampEveryPopup.scss';
import ButtonComponent from './ButtonComponent';
import InputBox from './InputBox';
var StampEveryPopup = function (_a) {
    var setClose = _a.setClose, onClose = _a.onClose;
    var _b = useState({
        interval: 1,
        integer: 0,
        numerator: 0,
        denominator: 1,
    }), inputData = _b[0], setInputData = _b[1];
    var _c = useState({
        show: false,
        intervalWarning: 'Enter valid Interval',
        valWarning: 'Please choose a starting number that is on the chart.',
    }), warning = _c[0], setWarning = _c[1];
    var _d = useState(null), currData = _d[0], setCurData = _d[1];
    var _e = useState(false), showWarning = _e[0], setShowWarning = _e[1];
    var _f = useContext(DataContext), selectedTool = _f.selectedTool, selectedManipulativeId = _f.selectedManipulativeId, manipulative = _f.manipulative;
    var popupRef = useRef(null);
    // The popup is `position: fixed` (viewport-anchored) so its on-screen
    // location must be computed from the trigger BUTTON's bounding rect —
    // not the wrapper, which can be wider/taller than the button itself.
    // This pins the popup vertically centered on the button and just to
    // its left, matching the previous `position: absolute; right: 100%;
    // top: 50%` look.
    useLayoutEffect(function () {
        var _a, _b, _c;
        var el = popupRef.current;
        if (!el)
            return;
        var wrapper = el.parentElement; // .stampEveryBtnWrapper
        if (!wrapper)
            return;
        // Find the trigger button — the first sibling of the popup that isn't
        // the popup itself. Avoids `querySelector('button')` which would also
        // match the OK button INSIDE the popup (depending on traversal order).
        var triggerSibling = Array.from(wrapper.children).find(function (c) { return c !== el; });
        var trigger = (_b = (_a = ((triggerSibling === null || triggerSibling === void 0 ? void 0 : triggerSibling.tagName) === 'BUTTON'
            ? triggerSibling
            : triggerSibling === null || triggerSibling === void 0 ? void 0 : triggerSibling.querySelector('button'))) !== null && _a !== void 0 ? _a : triggerSibling) !== null && _b !== void 0 ? _b : wrapper;
        var wrapperRef = ((_c = document.getElementById('stampEveryWrapperID')) === null || _c === void 0 ? void 0 : _c.offsetHeight) || 0;
        var updatePosition = function () {
            var rect = trigger.getBoundingClientRect();
            el.style.top = "".concat(rect.top - wrapperRef / 2 - rect.height / 2, "px");
            el.style.right = "".concat(window.innerWidth - rect.left, "px");
        };
        updatePosition();
        window.addEventListener('resize', updatePosition);
        window.addEventListener('scroll', updatePosition, true);
        return function () {
            window.removeEventListener('resize', updatePosition);
            window.removeEventListener('scroll', updatePosition, true);
        };
    }, []);
    // Focus management + inert background + keyboard handler (SaveBtnWithPopup pattern)
    useEffect(function () {
        var el = popupRef.current;
        if (!el)
            return;
        // Capture opener so focus can be restored on any close path.
        var opener = document.activeElement;
        // Auto-focus first input
        requestAnimationFrame(function () {
            var firstInput = el.querySelector('input');
            firstInput === null || firstInput === void 0 ? void 0 : firstInput.focus();
        });
        // Focus trap (Tab) + Escape to close — capture phase
        var handleKeyDown = function (e) {
            if (e.key === 'Escape') {
                e.preventDefault();
                e.stopPropagation();
                setClose(false);
                onClose();
                requestAnimationFrame(function () {
                    var _a;
                    var target = (_a = (opener && document.body.contains(opener) ? opener : null)) !== null && _a !== void 0 ? _a : document.querySelector('.stampEveryBtnWrapper button');
                    target === null || target === void 0 ? void 0 : target.focus();
                });
                return;
            }
            if (e.key === 'Tab') {
                e.preventDefault();
                var focusable = Array.from(el.querySelectorAll('button:not([disabled]):not([tabindex="-1"]), input:not([disabled]), [tabindex]:not([tabindex="-1"])')).filter(function (f) { return !f.closest('[inert]') && !f.closest('[aria-hidden="true"]'); });
                if (!focusable.length)
                    return;
                var idx = focusable.indexOf(document.activeElement);
                if (e.shiftKey) {
                    focusable[idx <= 0 ? focusable.length - 1 : idx - 1].focus();
                }
                else {
                    focusable[idx >= focusable.length - 1 ? 0 : idx + 1].focus();
                }
            }
        };
        document.addEventListener('keydown', handleKeyDown, true);
        return function () {
            document.removeEventListener('keydown', handleKeyDown, true);
            // Restore focus to the opener on any unmount path (Escape, OK, outside
            // close) so JAWS/VoiceOver users don't get dropped on the document body.
            requestAnimationFrame(function () {
                var _a;
                var target = (_a = (opener && document.body.contains(opener) ? opener : null)) !== null && _a !== void 0 ? _a : document.querySelector('.stampEveryBtnWrapper button');
                target === null || target === void 0 ? void 0 : target.focus();
            });
        };
    }, [onClose, setClose]);
    // Extract integer and fractional parts from a cell
    var extractCellParts = function (cell) {
        var numerator = cell.numerator, denominator = cell.denominator;
        if (denominator === 1 || denominator === 0) {
            // Integer or decimal - no fractional part
            return { integer: numerator, numerator: 0, denominator: 1 };
        }
        // For fractions and mixed numbers
        var integerPart = Math.floor(numerator / denominator);
        var remainingNumerator = numerator % denominator;
        return {
            integer: integerPart,
            numerator: remainingNumerator,
            denominator: denominator,
        };
    };
    // Initialize input fields with first cell value when popup opens
    useEffect(function () {
        var _a;
        if (selectedTool[0] !== '') {
            var foundItem = manipulative.find(function (m) { return m.type === 'numberCharts' && m.data.id === selectedTool[0]; });
            if (foundItem) {
                var data = foundItem.data;
                setCurData(data);
                setShowWarning(false);
                var firstCell = (_a = data.cells[0]) === null || _a === void 0 ? void 0 : _a[0];
                if (firstCell) {
                    var numberType = data.numberType;
                    if (numberType === 0) {
                        // Integer
                        setInputData({
                            interval: 1,
                            integer: firstCell.numerator,
                            numerator: 0,
                            denominator: 1,
                        });
                    }
                    else if (numberType === 1) {
                        // Fraction
                        setInputData({
                            interval: 1,
                            integer: 0,
                            numerator: firstCell.numerator,
                            denominator: firstCell.denominator,
                        });
                    }
                    else if (numberType === 2) {
                        // Mixed number - extract integer and fraction parts
                        var parts = extractCellParts(firstCell);
                        setInputData({
                            interval: 1,
                            integer: parts.integer,
                            numerator: parts.numerator,
                            denominator: parts.denominator,
                        });
                    }
                    else if (numberType === 3) {
                        // Decimal - use the value string for display, store numerator/denominator for comparison
                        var decimalValue = firstCell.denominator !== 0
                            ? firstCell.numerator / firstCell.denominator
                            : firstCell.numerator;
                        setInputData({
                            interval: 1,
                            integer: decimalValue,
                            numerator: firstCell.numerator,
                            denominator: firstCell.denominator,
                        });
                    }
                }
            }
        }
    }, [selectedTool, manipulative]);
    // Check if the entered value exists in any cell
    var findCellWithValue = function () {
        if (!currData)
            return false;
        var integer = inputData.integer, numerator = inputData.numerator, denominator = inputData.denominator;
        var numberType = currData.numberType;
        for (var _i = 0, _a = currData.cells; _i < _a.length; _i++) {
            var row = _a[_i];
            for (var _b = 0, row_1 = row; _b < row_1.length; _b++) {
                var cell = row_1[_b];
                if (numberType === 0) {
                    // Integer: compare numerator directly
                    if (cell.numerator === integer && cell.denominator === 1) {
                        return true;
                    }
                }
                else if (numberType === 1) {
                    // Fraction: compare numerator and denominator
                    if (cell.numerator === numerator && cell.denominator === denominator) {
                        return true;
                    }
                }
                else if (numberType === 2) {
                    // Mixed number: convert input to improper fraction and compare
                    var improperNumerator = integer * denominator + numerator;
                    if (cell.numerator === improperNumerator && cell.denominator === denominator) {
                        return true;
                    }
                }
                else if (numberType === 3) {
                    // Decimal: compare the actual decimal values
                    var cellValue = cell.denominator !== 0 ? cell.numerator / cell.denominator : cell.numerator;
                    if (cellValue === integer) {
                        return true;
                    }
                }
            }
        }
        return false;
    };
    var isValueValid = findCellWithValue();
    var isIntervalValid = inputData.interval >= 1;
    var isOkDisabled = !isIntervalValid || !isValueValid;
    var handleInputChange = function (fieldId, value) {
        setInputData(function (prev) {
            var _a;
            return (__assign(__assign({}, prev), (_a = {}, _a[fieldId] = Number(value), _a)));
        });
        setShowWarning(false);
        setWarning(function (prev) { return (__assign(__assign({}, prev), { show: false })); });
    };
    var stampAtInterval = function () {
        if (!currData) {
            console.warn('No chart data available');
            return;
        }
        if (!isValueValid) {
            setShowWarning(true);
            return;
        }
        var interval = inputData.interval, integer = inputData.integer, numerator = inputData.numerator, denominator = inputData.denominator;
        if (interval > currData.rows * currData.columns) {
            setWarning(function (prev) { return (__assign(__assign({}, prev), { show: true })); });
            return;
        }
        var numberType = currData.numberType;
        // Find the matching cell to get exact numerator/denominator for comparison
        var startNumerator = numerator;
        var startDenominator = denominator;
        if (numberType === 0) {
            // Integer
            startNumerator = integer;
            startDenominator = 1;
        }
        else if (numberType === 2) {
            // Mixed number: convert to improper fraction
            startNumerator = integer * denominator + numerator;
            startDenominator = denominator;
        }
        else if (numberType === 3) {
            // Decimal: find the cell with matching value to get its numerator/denominator
            for (var _i = 0, _a = currData.cells; _i < _a.length; _i++) {
                var row = _a[_i];
                for (var _b = 0, row_2 = row; _b < row_2.length; _b++) {
                    var cell = row_2[_b];
                    var cellValue = cell.denominator !== 0 ? cell.numerator / cell.denominator : cell.numerator;
                    if (cellValue === integer) {
                        startNumerator = cell.numerator;
                        startDenominator = cell.denominator;
                        break;
                    }
                }
            }
        }
        customEventHandler.dispatchEvent('choralDrawer', {
            id: 'stampEvery',
            val: {
                interval: interval,
                startInteger: integer,
                startNumerator: startNumerator,
                startDenominator: startDenominator,
                numberType: numberType,
                chartId: currData.id,
            },
        });
        announce(t('text.stampedEveryCel'));
        setClose(false);
        onClose();
    };
    return (_jsxs("div", { ref: popupRef, className: "stampEveryWrapper", role: "dialog", "aria-modal": "true", "aria-label": t('text.stampEveryCell'), id: "stampEveryWrapperID", children: [_jsxs("div", { className: "leftWrapper", children: [_jsxs("div", { className: "bottomContainer", children: [_jsxs("span", { className: "spanText", children: [" ", t('text.startingAt')] }), _jsxs("span", { style: { position: 'relative', display: 'flex', alignItems: 'center', gap: '10px' }, children: [((currData === null || currData === void 0 ? void 0 : currData.numberType) === 0 || (currData === null || currData === void 0 ? void 0 : currData.numberType) === 3) && (_jsx(InputBox, { id: "integer", type: (currData === null || currData === void 0 ? void 0 : currData.numberType) === 3 ? 'text' : 'number', min: 1, value: inputData.integer.toString(), onInput: function (_id, _value, event) {
                                            return handleInputChange('integer', event.target.value);
                                        } })), (currData === null || currData === void 0 ? void 0 : currData.numberType) === 2 && (_jsx(InputBox, { id: "integer", type: "number", min: 0, value: inputData.integer.toString(), onInput: function (_id, _value, event) {
                                            return handleInputChange('integer', event.target.value);
                                        } })), ((currData === null || currData === void 0 ? void 0 : currData.numberType) === 1 || (currData === null || currData === void 0 ? void 0 : currData.numberType) === 2) && (_jsxs("div", { className: "fractionContainer", children: [_jsx(InputBox, { id: "numerator", type: "number", min: (currData === null || currData === void 0 ? void 0 : currData.numberType) === 1 ? 1 : 0, value: inputData.numerator.toString(), onInput: function (_id, _value, event) {
                                                    return handleInputChange('numerator', event.target.value);
                                                }, defaultCSS: "inputValField" }), _jsx("div", { className: "dividerHorizontal" }), _jsx(InputBox, { id: "denominator", type: "number", min: 1, value: inputData.denominator.toString(), onInput: function (_id, _value, event) {
                                                    return handleInputChange('denominator', event.target.value);
                                                }, defaultCSS: "inputValField" })] }))] })] }), _jsxs("div", { className: "upperContainer", children: [_jsxs("span", { className: "spanText", children: [t('text.stampEveryLabel'), " "] }), _jsx(InputBox, { id: "interval", type: "number", step: 1, min: 1, max: 20, value: inputData.interval.toString(), onInput: function (_id, _value, event) {
                                    return handleInputChange('interval', event.target.value);
                                } }), _jsxs("span", { className: "spanText", children: [" ", t('text.squaresOnChart')] })] }), warning.show && _jsx("div", { className: "warningText", children: warning.intervalWarning })] }), _jsx("div", { className: "divider" }), _jsx("div", { className: "btnHolder", children: _jsx(ButtonComponent, { text: t('text.ok'), type: "textType1", defaultCSS: "outputBtn ".concat(isOkDisabled ? 'disabled' : ''), clickHandler: isOkDisabled ? undefined : stampAtInterval, disabled: isOkDisabled, tabIndex: isOkDisabled ? -1 : 0 }) })] }));
};
export default StampEveryPopup;
